import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PaymentSuccessPage = ({ auth }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 8;
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }
    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`);
      
      if (response.data.status === 'completed') {
        setStatus('success');
        setLoading(false);
        toast.success(`Payment successful! ${response.data.credits_added} credit(s) added.`);
      } else if (attempts < maxAttempts) {
        // Continue polling
        setTimeout(() => {
          setAttempts(attempts + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setStatus('timeout');
        setLoading(false);
        toast.error('Payment verification timed out. Please check your account.');
      }
    } catch (error) {
      setStatus('error');
      setLoading(false);
      toast.error('Failed to verify payment');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center py-8 px-4">
      <Card className="max-w-md w-full shadow-xl" data-testid="payment-status-card">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {loading ? 'Processing Payment...' : status === 'success' ? 'Payment Successful!' : 'Payment Issue'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {loading && (
            <div className="py-8">
              <Loader2 className="w-16 h-16 mx-auto text-indigo-600 animate-spin mb-4" />
              <p className="text-gray-600">Verifying your payment...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="py-8">
              <CheckCircle className="w-16 h-16 mx-auto text-green-600 mb-4" />
              <p className="text-lg mb-6">Your payment has been processed and credits have been added to your account!</p>
              <Button
                data-testid="go-to-dashboard"
                onClick={() => navigate('/dashboard')}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Go to Dashboard
              </Button>
            </div>
          )}
          
          {(status === 'error' || status === 'timeout') && (
            <div className="py-8">
              <p className="text-lg mb-6">There was an issue verifying your payment. Please check your dashboard or contact support.</p>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
              >
                Go to Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
